import type { GroupKind, Analysis } from '@teacher-shared/analysis';
import { TeachingPage, ClassSwitcher, useClass, Card, StudentLink, WithData, useAnalysis, AssessmentSwitcher, ProvisionalBanner, breadcrumbOf, CodeTag } from '@/components/teaching/ui';
import { KpiRow, KpiCard } from '@/components/teaching/kit';

function kindsOf(A: Analysis): { kind: GroupKind; title: string; sub: string }[] {
  const ext = A.extensionItem;
  return [
  { kind: 'reteach', title: 'Reteach to the whole class', sub: 'Topic under 50% class mastery, with enough answers to be sure' },
  { kind: 'small', title: 'Small groups', sub: '3+ students in a class sharing the same mistake or weak topic' },
  { kind: 'individual', title: 'One-to-one', sub: 'Needs help, but the pattern is not shared with others' },
  { kind: 'extension', title: 'Extension', sub: ext ? `Mastered every question, with ${Math.ceil(ext.max * 0.8)}+/${ext.max} on ${ext.id}` : 'No extension question set for this test' },
  ];
}

export function Groups() {
  return <WithData state={useAnalysis()} title="Teaching groups">{({ a, A }) => <GroupsView a={a} A={A} />}</WithData>;
}

function GroupsView({ a, A }: { a: string; A: Analysis }) {
  const [cls, setCls] = useClass(A.classes);
  const groups = A.groupsIn(cls);

  return (
    <TeachingPage title="Teaching groups" breadcrumb={breadcrumbOf(A)} banner={<ProvisionalBanner A={A} />} actions={<><AssessmentSwitcher current={a} /><ClassSwitcher value={cls} onChange={setCls} classes={A.classes} /></>}>
      <KpiRow>
        <KpiCard value={groups.filter((g) => g.kind === 'reteach').length} label="Whole-class reteach" />
        <KpiCard value={groups.filter((g) => g.kind === 'small').length} label="Small groups" />
        <KpiCard value={groups.filter((g) => g.kind === 'individual').length} label="One-to-one" />
        <KpiCard value={new Set(groups.filter((g) => g.kind === 'extension').flatMap((g) => g.students.map((s) => s.id))).size} label="Ready to extend" />
      </KpiRow>
      <div class="text-[12.5px] text-[var(--color-text-muted)] mb-4">
        Grouped by the evidence, not by total score. Groups are worked out per class; a student can be in more than one.
      </div>
      <div class="flex flex-col gap-6">
        {kindsOf(A).map(({ kind, title, sub }) => {
          const list = groups.filter((g) => g.kind === kind);
          return (
            <div key={kind}>
              <div class="mb-2">
                <div class="text-[14px] font-semibold text-[var(--color-text)]">{title} <span class="text-[var(--color-text-faint)] font-normal">({list.length})</span></div>
                <div class="text-[11.5px] text-[var(--color-text-faint)]">{sub}</div>
              </div>
              {list.length === 0 ? (
                <div class="text-[12.5px] text-[var(--color-text-muted)]">None on this test.</div>
              ) : (
                <div class="grid md:grid-cols-2 gap-3">
                  {list.map((g) => (
                    <Card key={g.cls + g.title}>
                      <div class="flex items-baseline gap-2">
                        <div class="text-[13px] font-semibold text-[var(--color-text)]">{g.title}{g.code && <CodeTag code={g.code} />}</div>
                        <div class="ml-auto shrink-0 text-[11px] text-[var(--color-text-faint)]">{g.students.length} student{g.students.length === 1 ? '' : 's'}</div>
                      </div>
                      <div class="text-[12px] text-[var(--color-text-muted)] mt-0.5">{g.reason}</div>
                      {kind !== 'reteach' && (
                        <div class="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[12.5px]">
                          {g.students.map((s) => <StudentLink key={s.id} a={a} id={s.id} name={s.name} cls={cls} />)}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </TeachingPage>
  );
}
